/**
 * Analytics Security Utilities
 * Provides HIPAA-compliant data sanitization and PHI detection for Google Analytics
 */

// Common PHI patterns to detect and block
const PHI_PATTERNS = [
  // Patient identifiers
  /\b\d{3}-\d{2}-\d{4}\b/, // SSN
  /\b[A-Z]{2}\d{6,}\b/, // Medical Record Numbers
  /\bMRN[:\s]?\d+\b/i, // MRN labels
  /\bpatient[_\s]?id[:\s]?\d+\b/i, // Patient ID
  
  // Contact information
  /\b\d{3}[-.]?\d{3}[-.]?\d{4}\b/, // Phone numbers
  /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b/, // Email addresses
  
  // Dates of birth
  /\b(0?[1-9]|1[0-2])[\/\-](0?[1-9]|[12]\d|3[01])[\/\-](19|20)\d{2}\b/, // MM/DD/YYYY
  /\b(19|20)\d{2}[\/\-](0?[1-9]|1[0-2])[\/\-](0?[1-9]|[12]\d|3[01])\b/, // YYYY/MM/DD
  
  // Insurance information
  /\bpolicy[_\s]?number[:\s]?[A-Z0-9]+\b/i,
  /\bgroup[_\s]?number[:\s]?[A-Z0-9]+\b/i,
  
  // Clinical data indicators
  /\bdiagnosis[:\s]/i,
  /\btreatment[:\s]/i,
  /\bprescription[:\s]/i,
  /\bmedication[:\s]/i,
];

// Sensitive field names that should never be tracked
const SENSITIVE_FIELDS = [
  'ssn', 'social_security', 'social_security_number',
  'dob', 'date_of_birth', 'birthdate',
  'mrn', 'medical_record_number', 'patient_id',
  'email', 'phone', 'phone_number', 'mobile',
  'address', 'street', 'zip', 'zipcode', 'postal_code',
  'insurance_id', 'policy_number', 'group_number',
  'diagnosis', 'treatment', 'prescription', 'medication',
  'first_name', 'last_name', 'full_name', 'patient_name',
  'credit_card', 'card_number', 'cvv', 'account_number',
];

/**
 * Detects if a string contains potential PHI
 * @param {string} text - Text to check for PHI
 * @returns {boolean} - True if PHI detected
 */
export function containsPHI(text) {
  if (!text || typeof text !== 'string') return false;
  
  return PHI_PATTERNS?.some(pattern => pattern?.test(text));
}

/**
 * Sanitizes data by removing PHI and sensitive information
 * @param {any} data - Data to sanitize (string, object, or array)
 * @returns {any} - Sanitized data
 */
export function sanitizeData(data) {
  if (!data) return data;
  
  // Handle strings
  if (typeof data === 'string') {
    if (containsPHI(data)) {
      return '[REDACTED_PHI]';
    }
    return data;
  }
  
  // Handle arrays
  if (Array.isArray(data)) {
    return data?.map(item => sanitizeData(item));
  }
  
  // Handle objects
  if (typeof data === 'object') {
    const sanitized = {};
    
    for (const [key, value] of Object.entries(data)) {
      // Check if field name is sensitive
      const lowerKey = key?.toLowerCase();
      if (SENSITIVE_FIELDS?.some(field => lowerKey?.includes(field))) {
        sanitized[key] = '[REDACTED]';
        continue;
      }
      
      // Recursively sanitize nested objects
      sanitized[key] = sanitizeData(value);
    }
    
    return sanitized;
  }
  
  return data;
}

/**
 * Validates if an event is safe to track
 * @param {string} eventName - Name of the event
 * @param {object} eventParams - Event parameters
 * @returns {object} - { safe: boolean, reason: string, sanitizedParams: object }
 */
export function validateEvent(eventName, eventParams = {}) {
  // Check event name for PHI
  if (containsPHI(eventName)) {
    return {
      safe: false,
      reason: 'Event name contains potential PHI',
      sanitizedParams: null,
    };
  }
  
  // Sanitize event parameters
  const sanitizedParams = sanitizeData(eventParams);
  
  // Check if any parameters were redacted
  const hasRedacted = JSON.stringify(sanitizedParams)?.includes('[REDACTED');
  
  return {
    safe: true,
    reason: hasRedacted ? 'Parameters sanitized' : 'Event validated',
    sanitizedParams,
  };
}

/**
 * Sanitizes URL by removing query parameters that might contain PHI
 * @param {string} url - URL to sanitize
 * @returns {string} - Sanitized URL
 */
export function sanitizeURL(url) {
  if (!url) return url;
  
  try {
    const urlObj = new URL(url, window.location.origin);
    
    // Remove all query parameters as they might contain PHI
    urlObj.search = '';
    
    // Check pathname for PHI patterns (like patient IDs in URLs)
    const pathname = urlObj?.pathname;
    if (containsPHI(pathname)) {
      // Replace numeric IDs with placeholder
      urlObj.pathname = pathname?.replace(/\/\d+/g, '/:id');
    }
    
    return urlObj?.pathname;
  } catch (error) {
    // If URL parsing fails, return path only
    return url?.split('?')?.[0];
  }
}

/**
 * Validates page title for PHI before tracking
 * @param {string} title - Page title to validate
 * @returns {string} - Sanitized title
 */
export function sanitizePageTitle(title) {
  if (!title) return title;
  
  if (containsPHI(title)) {
    // Remove PHI but keep general page context
    return title?.replace(/\b[A-Z][a-z]+\s+[A-Z][a-z]+\b/g, '[Patient Name]')?.replace(/\d{3}-\d{2}-\d{4}/g, '[SSN]')?.replace(/\b\d{3}[-.]?\d{3}[-.]?\d{4}\b/g, '[Phone]');
  }
  
  return title;
}

/**
 * Logs security events for audit purposes
 * @param {string} eventType - Type of security event
 * @param {object} details - Event details
 */
export function logSecurityEvent(eventType, details) {
  const timestamp = new Date()?.toISOString();
  const logEntry = {
    timestamp,
    type: eventType,
    details: sanitizeData(details),
  };
  
  // In production, this should send to a secure logging service
  if (import.meta.env?.DEV) {
    console.warn('[Analytics Security]', logEntry);
  }
  
  // Store in sessionStorage for audit trail (limited to session)
  try {
    const logs = JSON.parse(sessionStorage.getItem('analytics_security_logs') || '[]');
    logs?.push(logEntry);
    // Keep only last 100 entries
    if (logs?.length > 100) logs?.shift();
    sessionStorage.setItem('analytics_security_logs', JSON.stringify(logs));
  } catch (error) {
    // Silently fail if storage is unavailable
  }
}

/**
 * Gets analytics security audit logs
 * @returns {Array} - Array of security log entries
 */
export function getSecurityLogs() {
  try {
    return JSON.parse(sessionStorage.getItem('analytics_security_logs') || '[]');
  } catch (error) {
    return [];
  }
}

/**
 * Clears security audit logs
 */
export function clearSecurityLogs() {
  try {
    sessionStorage.removeItem('analytics_security_logs');
  } catch (error) {
    // Silently fail
  }
}